/** @module Small controls from the design system: segmented control, switch, scan status. */
import { Check, Clock, X } from "lucide-react";
import type { ReactNode } from "react";

export interface SegmentOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  /** Accessible name when the label is only an icon. */
  ariaLabel?: string;
}

/** One choice out of a few, as pill-shaped buttons in a well (design: `.segmented`). */
export function Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  className = "",
}: {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Name of the group for screen readers. */
  label: string;
  className?: string;
}) {
  return (
    <div role="group" aria-label={label} className={`segmented ${className}`}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className="seg"
          aria-pressed={option.value === value}
          aria-label={option.ariaLabel}
          onClick={() => {
            onChange(option.value);
          }}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** An on/off switch; the whole row around it should be its label. */
export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className="switch disabled:opacity-50"
      onClick={() => {
        onChange(!checked);
      }}
    />
  );
}

export type ScanState = "auto" | "review" | "none" | "busy";

const SYMBOL: Record<ScanState, ReactNode> = {
  auto: <Check strokeWidth={3} />,
  review: "?",
  none: <X strokeWidth={3} />,
  busy: <Clock />,
};

/**
 * The round status token: symbol and colour together, never colour alone.
 * The caller puts the text next to it; the token itself is decoration.
 */
export function ScanStatus({ state, className = "" }: { state: ScanState; className?: string }) {
  return (
    <span className={`status status-${state} ${className}`} aria-hidden="true">
      {SYMBOL[state]}
    </span>
  );
}

/**
 * How complete something is, as the design's ring; full turns into the foil gradient,
 * the one place besides the scan button and full arts that earns it.
 */
export function ProgressRing({
  percent,
  size = 56,
  label,
  onPage = false,
}: {
  percent: number;
  size?: number;
  /** Shown in the middle; the percentage when left out. */
  label?: string;
  /** On the page background rather than on a panel. */
  onPage?: boolean;
}) {
  const full = percent >= 100;
  return (
    <span
      className={`ring ${full ? "ring-foil" : ""}`}
      style={{
        ["--p" as string]: String(Math.min(100, Math.max(0, percent))),
        ["--ring-hole" as string]: onPage ? "var(--bg)" : "var(--surface)",
        width: size,
        height: size,
      }}
      aria-hidden="true"
    >
      <span className="ring-label" style={{ fontSize: size < 50 ? 10 : 12 }}>
        {label ?? `${String(Math.floor(percent))}${size < 50 ? "" : "%"}`}
      </span>
    </span>
  );
}
